import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, ShieldAlert, ArrowRight } from "lucide-react";
import { MANUAL_STAGES, STAGE_LABEL } from "../lib/pipeline";
import StatusExtraFields, { defaultStatusExtra, isStatusExtraValid, buildStatusExtraPayload } from "../components/StatusExtraFields";
import AcquisitionChannelField from "../components/AcquisitionChannelField";
import { CreationOriginField } from "../components/marketing/MarketingBits";

// Landing on a specific stage only makes sense for a manual one : "onboarding
// démarre automatiquement" via son propre flow, jamais depuis ce formulaire.
const CREATABLE_STAGES = MANUAL_STAGES;

export default function ProspectFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    pharmacy_name: "",
    reference_name: "",
    phone: "",
    email: "",
    commune: "",
    acquisition_channel: "",
    initial_note: "",
  });
  const requestedStatus = searchParams.get("status");
  const initialStatus = CREATABLE_STAGES.some((s) => s.id === requestedStatus) ? requestedStatus : "new_lead";
  const [status, setStatus] = useState(initialStatus);
  const [statusExtra, setStatusExtra] = useState(defaultStatusExtra(initialStatus));
  const [duplicateMatch, setDuplicateMatch] = useState(null);

  const set = (k) => (e) => {
    setDuplicateMatch(null);
    setData((d) => ({ ...d, [k]: e.target.value }));
  };
  const setAcquisitionChannel = (v) => {
    setDuplicateMatch(null);
    setData((d) => ({ ...d, acquisition_channel: v }));
  };
  const [origin, setOrigin] = useState("");
  const selectStatus = (s) => {
    setStatus(s);
    setStatusExtra(defaultStatusExtra(s));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.pharmacy_name.trim()) return;
    if (status !== "new_lead" && !isStatusExtraValid(status, statusExtra)) {
      toast.error("Merci de compléter les informations requises pour ce statut.");
      return;
    }
    setDuplicateMatch(null);
    setSaving(true);
    try {
      const [originKind, originRef] = origin.split(":");
      const payload = {
        ...data, status, ...(status !== "new_lead" ? buildStatusExtraPayload(status, statusExtra) : {}),
        ...(originKind === "campaign" ? { campaign_id: originRef } : originKind === "partner" ? { partner_id: originRef } : {}),
      };
      const { data: p } = await api.post("/prospects/internal", payload);
      toast.success("Prospect créé");
      if (p.origin_warning) toast.warning(`Prospect créé, mais l'origine n'a pas été enregistrée : ${p.origin_warning}`);
      navigate(`/prospects/${p.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && Array.isArray(detail.prospects)) {
        setDuplicateMatch(detail);
        return;
      }
      toast.error(formatApiErrorDetail(detail, "Erreur lors de la création du prospect"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="prospect-form-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouveau prospect</h1>
        <p className="text-sm text-slate-500">
          Enregistrez rapidement un lead. Vous pourrez enrichir la fiche ensuite.
        </p>
      </div>

      {duplicateMatch && (
        <Card className="p-4 border-amber-200 bg-amber-50/60 flex items-start gap-3" data-testid="duplicate-match-warning">
          <ShieldAlert className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-amber-800 min-w-0 flex-1">
            <div className="font-medium">{duplicateMatch.message}</div>
            {duplicateMatch.prospects.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {duplicateMatch.prospects.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => navigate(`/prospects/${p.id}`)}
                    data-testid={`duplicate-match-link-${p.id}`}
                    className="w-full flex items-center justify-between gap-2 bg-white/70 hover:bg-white rounded-md px-3 py-2 text-left border border-amber-200/70"
                  >
                    <span className="min-w-0">
                      <span className="font-medium text-slate-900 truncate block">{p.pharmacy_name}</span>
                      <span className="text-xs text-slate-500">{STAGE_LABEL[p.status] || p.status}</span>
                    </span>
                    <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <div className="min-w-0">
        <Card className="p-5 md:p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pharmacy_name" className="text-xs">
              Nom de la pharmacie<span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="pharmacy_name"
              data-testid="input-pharmacy-name"
              value={data.pharmacy_name}
              onChange={set("pharmacy_name")}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Statut</Label>
            <Select value={status} onValueChange={selectStatus}>
              <SelectTrigger data-testid="select-initial-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CREATABLE_STAGES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {status !== "new_lead" ? (
            <StatusExtraFields status={status} value={statusExtra} onChange={setStatusExtra} />
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="reference_name" className="text-xs">Contact</Label>
              <Input id="reference_name" data-testid="input-reference-name" value={data.reference_name} onChange={set("reference_name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="commune" className="text-xs">Commune / Quartier</Label>
              <Input id="commune" data-testid="input-commune" value={data.commune} onChange={set("commune")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-xs">Téléphone</Label>
              <Input id="phone" data-testid="input-phone" value={data.phone} onChange={set("phone")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" type="email" data-testid="input-email" value={data.email} onChange={set("email")} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Canal d'acquisition</Label>
              <AcquisitionChannelField value={data.acquisition_channel} onChange={setAcquisitionChannel} />
            </div>
            <CreationOriginField value={origin} onChange={setOrigin} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="initial_note" className="text-xs">Note initiale</Label>
            <Textarea
              id="initial_note"
              data-testid="input-initial-note"
              rows={3}
              placeholder="Contexte du premier contact…"
              value={data.initial_note}
              onChange={set("initial_note")}
            />
          </div>
        </Card>

        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={saving || !data.pharmacy_name.trim()}
            data-testid="btn-submit-prospect-form"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            Créer le prospect
          </Button>
        </div>
        </div>

        <aside className="space-y-4" data-testid="prospect-form-help">
          <Card className="p-5 space-y-2">
            <div className="font-display font-semibold text-sm text-slate-900">Avant d'enregistrer</div>
            <p className="text-sm text-slate-600">
              Jamora vérifie qu'aucune pharmacie ne porte déjà ce téléphone, cet email ou ce nom. En cas de doublon, la fiche existante
              vous est proposée : complétez-la plutôt que d'en créer une seconde.
            </p>
          </Card>
          <Card className="p-5 space-y-2">
            <div className="font-display font-semibold text-sm text-slate-900">Ensuite</div>
            <p className="text-sm text-slate-600">
              Depuis la fiche du prospect, complétez ses informations, attribuez-le, ajoutez une tâche de relance ou faites-le avancer dans
              le pipeline.
            </p>
          </Card>
        </aside>
      </form>
    </div>
  );
}
